import { Asset } from "./assets";
import { Collection } from "./collections";
import { Color, PaginatedRecords } from "./common";
import { Contact } from "./contact";
import { Media, MediaFile } from "./media";
import { User } from "./users";

export interface DealRoom {
  id?: string;
  name?: string;
  description?: string;
  icon?: Media;
  banner?: DealRoomBanner;
  entityType?: string;
  assetsCount?: number;
  sectionsCount?: number;
  externalAssets?: DealRoomAsset[];
  customerName?: string;
  viewerType?: string;
  assetViewer?: string;
  totalPeopleInvolved?: number;
  customerLogo?: Media | MediaFile | null;
  stageValue?: string;
  sharedRecipientsCount?: number;
  collaboratorsCount?: number;
  winProbability?: number;
  createdDate?: Date;
  modifiedDate?: Date;

  live?: boolean;
  internal?: boolean;
  dealRoomTemplate?: boolean;
  dealRoom?: boolean;
  type?: string;
  opportunity?: string;
  creator?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;

  assets?: DealRoomAsset[];
  sections?: DealRoomSection[];

  stages?: DealRoomStage[];

  readAll?: boolean;
  writeAll?: boolean;
  dealRoomUrl?: string;
  template?: boolean;
  canEditTemplate?: boolean;
}

export interface DealRoomChangeRequest {
  name?: string;
  description?: string;
  banner?: DealRoomBanner;
  customerLogo?: string;
  companyLogo?: string;
  customerName?: string;
  viewerType?: string;
  assetViewer?: string;
  meetingLink?: string;
  assets?: DealRoomAssetChangeRequest[];
  sections?: DealRoomSectionChangeRequest[];
  deletedAssets?: string[];
  deletedSections?: string[];
  notifyRecipients?: boolean;
  subject?: string;
  message?: string;
  opportunity?: string;
}

export interface DealRoomSectionChangeRequest {
  id: string;
  name?: string;
  assets?: DealRoomAssetChangeRequest[];
  deletedAssets?: string[];
}

export interface DealRoomAssetChangeRequest {
  id: string;
  order: number;
  entityMetadata: any;
}

export interface FetchDealRoomsRequest {
  filter?: string;
  page: number;
  limit: number;
  sortBy?: string;
  orderBy?: string;
}

export interface FetchDealRoomsResponse {
  filter?: string;
  response: {
    pagination: PaginationResponse;
    dealRooms: DealRoom[];
  };
}

export interface PaginationResponse {
  pageNumber: number;
  totalPagesCount: number;
  itemsPerPage: number;
  totalItemCount: number;
}

export interface assetsInitialState {
  loading: boolean;
  succeeded: boolean;
  error: any;
  items: DealRoomAsset[];
  pagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
    hasMoreItems: boolean;
  };
}

export interface FetchDealRoomsErrorResponse {
  filter?: string;
  error: any;
}

export interface DeleteDealRoomRequest {
  id: string;
}

export interface DeleteDealRoomResponse {
  id: string;
  response?: DealRoom;
}

export interface DeleteDealRoomErrorResponse {
  id: string;
  error: any;
}

export interface UpdateDealRoomRequest {
  id: string;
  request: DealRoomChangeRequest;
}

export interface UpdateDealRoomResponse {
  response: DealRoom;
}

export interface UpdateDealRoomErrorResponse {
  id: string;
  error: any;
}
export interface DealRoomBanner {
  position?: string;
  sharedByEnabled?: boolean;
  autoHeightEnabled?: boolean;
  background?: Media | MediaFile;
  bannerHeight?: string;
  descriptionOverlay?: Color;
  type?: string;
  fontColor?: Color;
  overlayColor?: Color;
  crop?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

export interface DealRoomAsset {
  id: string;
  asset: Asset;
  order: number;
  metadata?: any;
}

export interface DealRoomEditState extends Omit<DealRoom, "sections"> {
  sections?: DealRoomSectionState[];
}

export interface DealRoomEditRequest {
  dealRoom: DealRoomEditState;
  assets: assetsInitialState;
  allAssets: {
    [key in AssetCatergory]: assetsInitialState;
  };
}

export interface DealRoomSectionState {
  uid: string;
  loading: boolean;
  loaded: boolean;
  item: DealRoomSection | null;
  error: any;
}

export interface DealRoomSection {
  id?: string;
  name: string;

  dealRoom?: string;
  assets: assetsInitialState;
  createdDate?: Date;
  modifiedDate?: Date;
}

export interface FetchStakeholdersRequest {
  dealRoomId: string;
  type: StakeHolderTypeEnum;
  page: number;
}

export interface FetchStakeholdersErrorResponse {
  dealRoomId: string;
  type: StakeHolderTypeEnum;
  error: any;
}

export interface FetchStakeholdersResponse {
  dealRoomId: string;
  type: StakeHolderTypeEnum;
  pagination: PaginationResponse;
  records: any;
}

export enum StakeHolderTypeEnum {
  INTERNAL = "INTERNAL",
  EXTERNAL = "EXTERNAL",
}

export interface DeleteStakeholderRequest {
  dealRoomId: string;
  stakeholderType: StakeHolderTypeEnum;
  recipientId: string;
}

export interface DeleteStakeholderResponse {
  dealRoomId: string;
}
export interface DeleteStakeholderErrorResponse {
  dealRoomId: string;
  error: any;
}

export interface CreateDealRoomRequest {
  customerName?: string;
  description?: string;
  name: string;
  opportunity?: string;
}

export interface CreateDealRoomResponse {
  response: DealRoom;
}

export interface CreateDealRoomErrorResponse {
  error: any;
}

export interface FetchDealRoomRequest {
  id: string;
}

export interface FetchDealRoomResponse {
  response: DealRoom;
}

export interface FetchDealRoomErrorResponse {
  id: string;
  error: any;
}

export interface UpdateDealRoomPropertyRequest {
  dealRoom: string;
  property: string;
  value: any;
}

export interface AddAssetToDealRoomRequest {
  dealRoom: string;
  section?: number;
  sectionId?: string;
  assets: DealRoomAsset[];
  addAt: "start" | "end";
}

export interface ArrangeAssetInDealRoomRequest {
  dealRoom: string;
  section?: number;
  from: number;
  to: number;
}

export interface RearrangeAssetInDealRoomRequest {
  id: string;
  sectionIndex?: number;
  updatedAssets: DealRoomAsset[];
}

export interface MoveAssetToSectionRequest {
  dealRoom: string;
  source: number;
  target: number;
  asset: DealRoomAsset;
}

export interface RemoveAssetFromDealRoomRequest {
  dealRoom: string;
  section?: number;
  asset: string;
}

export interface UpdateAssetMetadataRequest {
  dealRoom: string;
  section?: number;
  updates: {
    id: string;
    metadata: any;
  }[];
}
// id, page, limit, assetCategory
export interface FetchDealRoomAssetsRequest {
  id: string;
  page: number;
  limit: number;
  assetCategory: AssetCatergory;
}

export interface FetchDealRoomAssetsResponse {
  id: string;
  assetCategory: AssetCatergory;
  pagination: PaginationResponse;
  records: DealRoomAsset[];
}

export interface FetchDealRoomAssetsErrorResponse {
  id: string;
  assetCategory: AssetCatergory;
  error: any;
}

export enum AssetCatergory {
  INTERNAL = "INTERNAL",
  EXTERNAL = "EXTERNAL",
  ALL = "ALL",
}

export interface FetchDealRoomAssetsForSectionRequest {
  id: string;
  page: number;
  limit: number;
  assetCategory: AssetCatergory;
  sectionId: string;
}

export interface FetchDealRoomAssetsForSectionResponse {
  id: string;
  assetCategory: AssetCatergory;
  pagination: PaginationResponse;
  records: DealRoomAsset[];
  sectionId: string;
}

export interface FetchDealRoomAssetsForSectionErrorResponse {
  id: string;
  assetCategory: AssetCatergory;
  error: any;
  sectionId: string;
}

export interface CreateDealRoomSectionRequest {
  dealRoom: string;
  name: string;
}

export interface RenameDealRoomSectionRequest {
  dealRoom: string;
  index: number;
  name: string;
}

export interface RemoveDealRoomSectionRequest {
  dealRoom: string;
  index: number;
}

export interface MoveDealRoomSectionRequest {
  dealRoom: string;
  from: number;
  to: number;
}

export interface FetchDealRoomCollaboratorsRequest {
  dealRoom: string;
  query: string;
  page: number;
}

export interface FetchDealRoomCollaboratorsResponse {
  dealRoom: string;
  response: {
    pagination: PaginationResponse;
    users: DealRoomCollaborate[];
  };
}

export interface FetchDealRoomCollaboratorsErrorResponse {
  dealRoom: string;
  error: any;
}
export interface DealRoomCollaborate {
  entityType: string;
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  canRead: boolean;
  canWrite: boolean;
  enabled: boolean;
}

export interface UpdateDealRoomCollaboratorsRequest {
  dealRoom: string;
  requestBody: {
    readAll: boolean;
    writeAll: boolean;
    collaborators: {
      id: string;
      write: boolean;
    };
  };
}

export interface UpdateDealRoomCollaboratorsResponse {
  dealRoom: string;
  response: DealRoomCollaborate[];
}
export interface UpdateDealRoomCollaboratorsErrorResponse {
  dealRoom: string;
  error: any;
}

export interface Pagination {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  totalRecordsCount: number;
  hasMoreItems: boolean;
}

export interface RecordsState<T> {
  items: T[];
  pagination: Pagination;
  loading: boolean;
  loaded: boolean;
  error: any;
}

export interface ActionsState extends RecordsState<DealRoomAction> {
  oldOrder: DealRoomAction[] | null;
  newAction: {
    item: DealRoomAction;
    loading: boolean;
    error: any;
  };
  editAction: {
    item: DealRoomAction | null;
    loading: boolean;
    error: any;
  };
}

export interface DealRoomSharedCollectionsState {
  items: Collection[];
  loading: boolean;
  loaded: boolean;
  error: any;
}

export interface DealRoomDetailsData {
  id: string;
  title: string;
  value: number;
  owner: string;
  stage: string;
  prediction: number;
  remainingDays: number | null;
  closedDate: number;
  createdDate: number;
  integrationType: string;
  lastActivityDate: number;
}

export interface DealRoomDetailsState {
  data: DealRoomDetailsData | null;
  loading: boolean;
  loaded: boolean;
  error: any;
}

export interface DealRoomSignal {
  priority: string;
  subject: string;
  message: string;
}

export interface DealRoomHistory {
  id: string;
  dealId: string;
  stageValue: string;
  status: string;
  teamConfidence: number;
  aiConfidence: number;
  createdDate: number;
  createdDateStr: string;
}

export interface FetchDealRoomSharedAssetsRequest {
  dealRoomId: string;
  dealId: string;
  page: number;
}

interface SharedAssetsResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: Asset[];
  totalRecordsCount: number;
}

export interface FetchDealRoomSharedAssetsResponse {
  dealRoomId: string;
  response: SharedAssetsResponse;
}

export interface FetchDealRoomSharedAssetsErrorResponse {
  dealRoomId: string;
  error: any;
}

export interface FetchDealRoomSharedCollectionsRequest {
  dealRoomId: string;
  dealId: string;
}

export interface FetchDealRoomSharedCollectionsResponse {
  dealRoomId: string;
  response: Collection[];
}

export interface FetchDealRoomSharedCollectionsErrorResponse {
  dealRoomId: string;
  error: any;
}

export interface FetchDealRoomDetailsRequest {
  dealRoomId: string;
  dealId: string;
}

export interface FetchingDealRoomDetailsResponse {
  dealRoomId: string;
  response: DealRoomDetailsData;
}

export interface FetchingDealRoomDetailsErrorResponse {
  dealRoomId: string;
  error: any;
}

export interface FetchDealRoomSignalsRequest {
  dealRoomId: string;
  dealId: string;
  page: number;
}

interface SignalsResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: DealRoomSignal[];
}

export interface FetchingDealRoomSignalsResponse {
  dealRoomId: string;
  response: SignalsResponse;
}

export interface FetchingDealRoomSignalsErrorResponse {
  dealRoomId: string;
  error: any;
}

export interface FetchDealRoomHistoryRequest {
  dealRoomId: string;
  dealId: string;
  page: number;
}

interface HistoryResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: DealRoomHistory[];
}

export interface FetchingDealRoomHistoryResponse {
  dealRoomId: string;
  response: HistoryResponse;
}

export interface FetchingDealRoomHistoryErrorResponse {
  dealRoomId: string;
  error: any;
}

export interface FetchDealRoomEngageChatUsersRequest {
  dealRoomId: string;
  page: number;
}

interface DealRoomEngageChatUsersResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  totalRecordsCount: number;
  records: DealRoomEngageChatUser[];
}

export interface FetchingDealRoomEngageChatUsersResponse {
  dealRoomId: string;
  response: DealRoomEngageChatUsersResponse;
}

export interface FetchingDealRoomEngageChatUsersErrorResponse {
  dealRoomId: string;
  error: any;
}

export interface DealRoomEngageChatUser {
  accessRemovedDate: number | null;
  active: boolean;
  contact: any;
  deliveryStatus: "sent" | "not-sent";
  events: any[];
  ndaConsent: null;
  shareType: "primary" | "secondary";
  sharedDate: number;
  timeSpent: number | null;
  type: any;
  visitor: any;
  lastOnline: string;
  online: boolean;
}

export interface FetchDealRoomActiveChatUserRequest {
  chatUserId: string;
  dealRoomId: string;
}

export interface FetchingDealRoomActiveChatUserResponse {
  dealRoomId: string;
  response: DealRoomEngageChatUser;
}

export interface FetchingDealRoomActiveChatUserErrorResponse {
  dealRoomId: string;
  error: any;
}

export interface FetchDealRoomSmartCardsRequest {
  dealRoomId: string;
}

export interface FetchDealRoomSmartCardsResponse {
  dealRoomId: string;
  response: any;
}

export interface FetchDealRoomSmartCardsErrorResponse {
  dealRoomId: string;
  error: any;
}

export interface FetchDealRoomAssetAnalyticsRequest {
  dealRoomId: string;
  assetId: string;
  assetCategory: AssetCatergory;
  sectionId?: string;
}

export interface FetchDealRoomAssetAnalyticsResponse {
  dealRoomId: string;
  assetId: string;
  response: any;
  assetCategory: AssetCatergory;
  sectionId?: string;
}

export interface FetchDealRoomAssetAnalyticsErrorResponse {
  dealRoomId: string;
  assetId: string;
  error: any;
  assetCategory: AssetCatergory;
  sectionId?: string;
}

export interface FetchDealRoomEventsRequest {
  dealRoomId: string;
  request: {
    share: string;
    eventTypes: any[];
    assets: any[];
    contacts: any[];
    startDate: string;
    endDate: string;
  };
  page?: number;
}

export interface DealRoomEvents {
  id: string;
  type: string;
  createdDate: number;
  contact: Contact;
  target: Contact;
  visitor: any;
  session: string | null;
  visitorSession: string | null;
  sharedWith: any;
  assetAdded: any;
}

export interface FetchDealRoomEventsResponse {
  dealRoomId: string;
  request: {
    share: string;
    eventTypes: any[];
    assets: any[];
    contacts: any[];
  };
  data: PaginatedRecords<DealRoomEvents>;
}

export interface FetchDealRoomEventsErrorResponse {
  dealRoomId: string;
  request: {
    share: string;
    eventTypes: any[];
    assets: any[];
    contacts: any[];
  };
  error: any;
}
export interface CloneDealRoomRequest {
  id?: string;
}

export interface CloneDealRoomResponse {
  response?: DealRoom;
}

export interface CloneDealRoomErrorResponse {
  error: any;
}

export interface DealRoomRecipient {
  shareRecipient: string;
  active: boolean;
  canWrite: boolean;
  canEditAction: boolean;
  user: {
    id: string;
    name: string;
    firstName: string;
    lastName: string;
    email: string;
    icon: Media;
  };
  contact?: {
    id: string;
    name: string;
    firstName: string;
    lastName: string;
    email: string;
    icon: Media;
  };
}

export interface DealRoomAction {
  id: string;
  actionItem: string;
  dueDate: string | null;
  completed: boolean;
  assets?: Asset[];
  recipients?: DealRoomRecipient[];
  stage?: string;
  canEdit?: boolean;
}

export interface UpdateDealRoomActionResponse extends DealRoomAction {
  stageInfo: DealRoomStage;
  subsequentActionsCount: number;
}

export interface CascadeDatesRequestPayload {
  targetDueDate: string;
  dateDifference: number;
  anchorActionId: string;
}

export interface CascadeDatesResponse {
  updatedActions: DealRoomAction[];
}

export interface DealRoomStage {
  id: string;
  title: string;
  totalActions?: number;
  completedActions?: number;
  isStageAccordionOpen: boolean;
  totalAttachments?: number;
  totalRecipients?: number;
}

export interface DealRoomActionsProgress {
  totalActions: number;
  completedActions: number;
  pendingActions: number;
  overdueActions: number;
  percentCompleted: number;
}

export interface DealRoomMapState {
  editMode: boolean;
  editingItem: {
    type: "stage" | "action";
    id: string;
    wiggleKey: number;
  } | null;
  addAssetSlider: {
    show: boolean;
    stageId: string;
    actionId: string;
    isNewAction: boolean;
    existingAssets: Asset[];
  };
  showAddAssetSlider: boolean;
  saveActionSlider: {
    stageId: string;
    action: DealRoomAction | null;
    changes: DealRoomActionEditSliderChanges;
    stakeholdersToBeNotified: DealRoomRecipient[];
  };
  deleteActionSlider: {
    stageId: string;
    action: DealRoomAction | null;
  };
  isReorderingStages: boolean;
  deletingStage: {
    id: string | null;
    loading: boolean;
    error: any;
  };
  editingStage: {
    id: string | null;
    item: DealRoomStage | null;
    loading: boolean;
    error: any;
  };
  creatingStage: {
    id: string | null;
    loading: boolean;
    error: any;
  };
  stages: {
    loading: boolean;
    loaded: boolean;
    items: DealRoomStage[];
    oldStageOrder: DealRoomStage[] | null;
    error: any;
  };
  actions: {
    [stageId: string]: ActionsState;
  };
  actionsProgress: {
    data: DealRoomActionsProgress | null;
    loading: boolean;
    error: any;
  };
  recipients: RecordsState<DealRoomRecipient>;
  assets: RecordsState<Asset>;
  completeAction: {
    stageId: string;
    actionId: string;
    completed: boolean;
    loading: boolean;
    error: any;
  };
  deleteAction: {
    stageId: string;
    actionId: string;
    loading: boolean;
    error: any;
  };
  cascadingActionItem: {
    targetAction: DealRoomAction | null;
    updatedDate: string | null;
    anchorActionId: string | null;
    subsequentActionsCount: number;
    showConfirmationSlider: boolean;
    cascadeDates: {
      loading: boolean;
      success: boolean;
      error: any;
    };
  };
}

export interface FetchDealRoomActionsForStageResponse {
  pagination: PaginationResponse;
  actions: DealRoomAction[];
}

export interface ActionsListState {
  items: DealRoomAction[];
  loading: boolean;
  loaded: boolean;
  error: any;
  pagination?: {
    currentPage: number;
    totalPages: number;
    recordsPerPage?: number;
    totalRecordsCount?: number;
    hasMoreItems?: boolean;
  };
}

export interface CreateDealRoomActionPayload
  extends Omit<DealRoomAction, "id" | "completed" | "recipients" | "assets"> {
  stage: string;
  recipients: string[];
  assets: string[];
}

export interface SaveDealRoomActionPayload
  extends Omit<
    DealRoomAction,
    "completed" | "canEdit" | "dueDate" | "recipients" | "assets" | "actionItem"
  > {
  stage: string;
  actionItem?: string;
  recipients?: string[];
  assets?: string[];
  removeRecipients?: string[];
  removeAssets?: string[];
  dueDate?: string;
}

export enum StageStatus {
  Completed = "Completed",
  Progressing = "Progressing",
  NotStarted = "Not Started",
}

export interface DealRoomFetchRecipientsResponse {
  records: DealRoomRecipient[];
  pagination: {
    pageNumber: number;
    totalPagesCount: number;
    itemsPerPag: number;
    totalItemCount: number;
  };
}

export interface DealRoomFetchAssetsResponse {
  records: Asset[];
  pagination: {
    pageNumber: number;
    totalPagesCount: number;
    itemsPerPag: number;
    totalItemCount: number;
  };
}

export interface DealRoomActionEditChanges {
  actionItem?: string;
  recipients?: DealRoomRecipient[];
  assets?: Asset[];
  dueDate?: string;
}

export interface DealRoomMarkAsTemplateRequest {
  id: string;
  value: boolean;
}

export interface DealRoomMarkAsTemplateResponse {
  id: string;
  template: boolean;
  canEditTemplate: boolean
}

export interface DealRoomMarkAsTemplateErrorResponse {
  id: string;
  error: any;
}

export interface DealRoomActionEditSliderChanges {
  recipients?: DealRoomRecipient[];
  assets?: Asset[];
  dueDate?: string;
}

export type DealRoomMapActionTypes =
  | {
      type: "TOGGLE_EDIT_MODE";
      payload: {
        editMode: boolean;
      };
    }
  | { type: "FETCH_ACTIONS_PROGRESS" }
  | {
      type: "FETCHING_ACTIONS_PROGRESS_SUCCEEDED";
      payload: {
        progress: DealRoomActionsProgress;
      };
    }
  | {
      type: "FETCHING_ACTIONS_PROGRESS_FAILED";
      payload: {
        error: any;
      };
    }
  | { type: "FETCH_RECIPIENTS_FOR_MAP" }
  | {
      type: "FETCHING_RECIPIENTS_FOR_MAP_SUCCEEDED";
      payload: {
        recipients: DealRoomFetchRecipientsResponse;
      };
    }
  | { type: "FETCHING_RECIPIENTS_FOR_MAP_FAILED"; payload: { error: any } }
  | { type: "FETCH_ASSETS_FOR_MAP" }
  | {
      type: "FETCHING_ASSETS_FOR_MAP_SUCCEEDED";
      payload: {
        assets: DealRoomFetchAssetsResponse;
      };
    }
  | { type: "FETCHING_ASSETS_FOR_MAP_FAILED"; payload: { error: any } }
  | { type: "FETCH_STAGES" }
  | {
      type: "FETCHING_STAGES_SUCCEEDED";
      payload: {
        stages: DealRoomStage[];
      };
    }
  | {
      type: "FETCHING_STAGES_FAILED";
      payload: {
        error: any;
      };
    }
  | { type: "ADD_NEW_STAGE" }
  | { type: "INVALIDATE_EDITING_ITEM" }
  | {
      type: "UPDATE_NEW_STAGE";
      payload: {
        newStageTitle: string;
      };
    }
  | {
      type: "TOGGLE_STAGE_ACCORDION";
      payload: {
        stageId: string;
      };
    }
  | { type: "CREATE_NEW_STAGE" }
  | {
      type: "CREATING_NEW_STAGE_SUCCEEDED";
      payload: {
        newStage: DealRoomStage;
      };
    }
  | {
      type: "CREATING_NEW_STAGE_FAILED";
      payload: {
        error: any;
      };
    }
  | { type: "INVALIDATE_NEW_STAGE" }
  | {
      type: "TOGGLE_EDIT_STAGE";
      payload: {
        stageId: string;
      };
    }
  | { type: "INVALIDATE_EDIT_STAGE" }
  | {
      type: "EDIT_STAGE";
      payload: {
        stageId: string;
        newTitle: string;
      };
    }
  | { type: "UPDATE_STAGE" }
  | {
      type: "UPDATING_STAGE_SUCCEEDED";
      payload: {
        modifiedStage: DealRoomStage;
      };
    }
  | {
      type: "UPDATING_STAGE_FAILED";
      payload: {
        error: any;
      };
    }
  | {
      type: "TOGGLE_DELETE_STAGE";
      payload: {
        stageId: string;
      };
    }
  | { type: "DELETE_STAGE" }
  | {
      type: "DELETING_STAGE_SUCCEEDED";
      payload: {
        stageId: string;
      };
    }
  | { type: "DELETING_STAGE_FAILED"; payload: { error: any } }
  | { type: "INVALIDATE_DELETE_STAGE" }
  | { type: "UPDATE_STAGE_ORDER"; payload: DealRoomStage[] }
  | { type: "UPDATING_STAGE_ORDER_SUCCEEDED"; payload: DealRoomStage[] }
  | {
      type: "UPDATING_STAGE_ORDER_FAILED";
      payload: {
        error: any;
      };
    }
  | { type: "FETCH_ACTIONS_FOR_STAGE"; payload: { stageId: string } }
  | {
      type: "FETCHING_ACTIONS_FOR_STAGE_SUCCEEDED";
      payload: {
        stageId: string;
        response: FetchDealRoomActionsForStageResponse;
      };
    }
  | {
      type: "FETCHING_ACTIONS_FOR_STAGE_FAILED";
      payload: { stageId: string; error: any };
    }
  | {
      type: "CREATE_NEW_ACTION";
      payload: { stageId: string };
    }
  | {
      type: "CREATING_NEW_ACTION_SUCCEEDED";
      payload: { stageId: string; updatedStageInfo: DealRoomStage };
    }
  | {
      type: "CREATING_NEW_ACTION_FAILED";
      payload: { stageId: string; error: any };
    }
  | {
      type: "INVALIDATE_CREATE_NEW_ACTION";
      payload: string;
    }
  | {
      type: "INITIATE_EDIT_ACTION";
      payload: {
        stageId: string;
        actionId: string;
      };
    }
  | {
      type: "EDIT_ACTION";
      payload: {
        stageId: string;
        actionId: string;
        changes: DealRoomActionEditChanges;
        isNewAction: boolean;
      };
    }
  | {
      type: "INVALIDATE_EDIT_ACTION";
      payload: string;
    }
  | {
      type: "TOGGLE_SAVE_ACTION_SLIDER";
      payload: {
        action: DealRoomAction;
        stageId: string;
        changes: DealRoomActionEditSliderChanges;
        stakeholdersToBeNotified: DealRoomRecipient[];
      };
    }
  | {
      type: "SAVE_EDIT_ACTION";
      payload: {
        stageId: string;
        actionId: string;
        updatedDate: string | null;
      };
    }
  | {
      type: "SAVING_EDIT_ACTION_SUCCEEDED";
      payload: {
        stageId: string;
        actionId: string;
        updatedAction: DealRoomAction;
        updatedStageInfo: DealRoomStage;
        subsequentActionsCount: number;
        updatedDate: string | null;
      };
    }
  | {
      type: "SAVING_EDIT_ACTION_FAILED";
      payload: {
        stageId: string;
        actionId: string;
        error: any;
      };
    }
  | {
      type: "REORDERING_ACTIONS";
      payload: {
        stageId: string;
        updatedActionsInOrder: DealRoomAction[];
      };
    }
  | {
      type: "REORDERING_ACTIONS_SUCCEEDED";
      payload: {
        stageId: string;
      };
    }
  | {
      type: "REORDERING_ACTIONS_FAILED";
      payload: {
        stageId: string;
        error: any;
      };
    }
  | {
      type: "COMPLETE_ACTION";
      payload: {
        stageId: string;
        actionId: string;
        completed: boolean;
      };
    }
  | {
      type: "COMPLETING_ACTION";
    }
  | {
      type: "COMPLETING_ACTION_SUCCEEDED";
      payload: {
        stageId: string;
        actionId: string;
        updatedAction: DealRoomAction;
        updatedStageInfo: DealRoomStage;
      };
    }
  | {
      type: "COMPLETING_ACTION_FAILED";
      payload: {
        stageId: string;
        actionId: string;
        error: any;
      };
    }
  | {
      type: "INVALIDATE_COMPLETE_ACTION";
    }
  | {
      type: "TOGGLE_ADD_ASSETS_SLIDER";
      payload: {
        show: boolean;
        stageId: string;
        actionId: string;
        isNewAction: boolean;
        existingAssets: Asset[];
      };
    }
  | {
      type: "TOGGLE_DELETE_ACTION_SLIDER";
      payload: {
        stageId: string;
        action: DealRoomAction | null;
      };
    }
  | {
      type: "DELETE_ACTION";
      payload: {
        stageId: string;
        actionId: string;
      };
    }
  | {
      type: "DELETING_ACTION_SUCCEEDED";
      payload: {
        stageId: string;
        actionId: string;
        newActionsOrder: string[];
        completedActions: number;
        totalActions: number;
        totalAttachments: number;
        totalRecipients: number;
      };
    }
  | {
      type: "DELETING_ACTION_FAILED";
      payload: {
        stageId: string;
        actionId: string;
        error: any;
      };
    }
  | {
      type: "INVALIDATE_DELETE_ACTION";
    }
  | {
      type: "INVALIDATE_CASCADING_ACTION_ITEM";
    }
  | {
      type: "TOGGLE_CASCADE_DATES_SLIDER";
      payload: {
        show: boolean;
      };
    }
  | {
      type: "INITIATE_CASCADE_DATES";
    }
  | {
      type: "CASCADE_DATES_SUCCEEDED";
      payload: {
        updatedActions: DealRoomAction[];
      };
    }
  | {
      type: "CASCADE_DATES_FAILED";
      payload: {
        error: any;
      };
    }
  | {
      type: "INVALIDATE_ON_GLOBAL_EXIT";
    };
